import * as React from 'react';

type LayoutMode = 'single' | 'spread';

type LayoutModeProviderState = {
  layoutMode: LayoutMode;
  setLayoutMode: (mode: LayoutMode) => void;
};

const initialState: LayoutModeProviderState = {
  layoutMode: 'single',
  setLayoutMode: () => null,
};

const LayoutModeContext =
  React.createContext<LayoutModeProviderState>(initialState);

const STORAGE_KEY = 'haamil-layout-mode';

function isLayoutMode(value: string | null): value is LayoutMode {
  return value === 'single' || value === 'spread';
}

export function LayoutModeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [layoutMode, setLayoutMode] = React.useState<LayoutMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLayoutMode(stored) ? stored : 'single';
  });

  const value = {
    layoutMode,
    setLayoutMode: (mode: LayoutMode) => {
      window.localStorage.setItem(STORAGE_KEY, mode);
      setLayoutMode(mode);
    },
  };

  return (
    <LayoutModeContext.Provider value={value}>
      {children}
    </LayoutModeContext.Provider>
  );
}

export const useLayoutMode = () => {
  const context = React.useContext(LayoutModeContext);
  if (context === undefined) {
    throw new Error('useLayoutMode must be used within a LayoutModeProvider');
  }
  return context;
};
